import { useEffect, useRef, useState } from 'react';

interface UseInViewportOptions {
  rootMargin?: string;
  once?: boolean;
}

export function useInViewport<T extends HTMLElement>(
  options: UseInViewportOptions = {}
): [React.RefObject<T>, boolean] {
  const { rootMargin = '200px', once = true } = options;
  const ref = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || (once && isInView)) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (once) {
            observer.disconnect();
          }
        } else if (!once) {
          setIsInView(false);
        }
      },
      { rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isInView, once, rootMargin]);

  return [ref, isInView];
}
